import { createContext, useContext } from 'react';
/**
 * Measure-then-place signing (per ADR-0044).
 *
 * Scrive's text-anchor matching proved unreliable against our
 * Gotenberg-rendered PDFs. Instead we do the matching ourselves: render a
 * throwaway "measurement" PDF that carries a deterministic marker token at
 * each signature/place/date point, locate those tokens' exact coordinates
 * with pdfjs (backend `pdf-measure` service), then render the real, clean
 * PDF (no markers) and hand Scrive ABSOLUTE coordinates. No anchor strings,
 * no dependency on Scrive's matcher — fully controllable + dynamic across
 * pagination.
 *
 * The marker is a single plain-ASCII run (no spaces, so Chromium keeps it
 * as one text item) and is rendered `position: absolute` so it has ZERO
 * layout impact — the measurement render and the clean render share
 * identical geometry, so measured coordinates are valid for the clean PDF.
 */
export const SIGN_MEASURE_PREFIX = '@RSIG';
/** The marker text rendered into the measurement PDF for one field. */
export function signMeasureToken(role, ordinal, signerOrdinal, field) {
    return `${SIGN_MEASURE_PREFIX}|${role}|${ordinal}|${signerOrdinal}|${field}|`;
}
/** Canonical key used to look a measured field up (Map key). */
export function signMeasureKey(role, ordinal, signerOrdinal, field) {
    return `${role}:${ordinal}:${signerOrdinal}:${field}`;
}
const TOKEN_RE = /@RSIG\|([a-z_]+)\|(\d+)\|(\d+)\|(signature|place|date)\|/;
/** Parse a token found in the PDF text layer; null if it doesn't match. */
export function parseSignMeasureToken(s) {
    const m = TOKEN_RE.exec(s);
    if (!m)
        return null;
    const role = m[1];
    const ordinal = Number(m[2]);
    const signerOrdinal = Number(m[3]);
    const field = m[4];
    return { role, ordinal, signerOrdinal, field, key: signMeasureKey(role, ordinal, signerOrdinal, field) };
}
/**
 * When true, signing blocks render measurement markers. Set by the render
 * pipeline for the throwaway measurement pass only; false (default) for the
 * clean render that goes to S3 + Scrive.
 */
export const SignMeasureContext = createContext(false);
export function useSignMeasure() {
    return useContext(SignMeasureContext);
}
/**
 * Whether signing blocks render the "Ort" (place) and "Datum" (date)
 * rows (per ACT-06). Defaults to TRUE so previews and the manual-signing
 * path keep the blanks — physical signing needs somewhere to write place
 * and date, and the two parties' dates may legitimately differ.
 *
 * The Scrive send flow sets this from a per-send checkbox (default OFF):
 * BankID stamps the signing time itself, so ort/datum are redundant on a
 * digitally-signed document. When false, the rows aren't rendered, the
 * measurement pass emits no place/date markers, and no `text`/`sign_date`
 * fields are placed (both are optional in the placement builder).
 */
export const SignIncludePlaceDateContext = createContext(true);
export function useSignIncludePlaceDate() {
    return useContext(SignIncludePlaceDateContext);
}
/**
 * Whether a signing block whose template opts into the signing statement
 * prints the DIGITAL wording when Ort/Datum is off. Only the Scrive send
 * flow sets this: the digital wording names Scrive's watermark. Everywhere
 * else (manual signing outside the platform) Ort/Datum off means no
 * statement at all — the document is left unadapted.
 */
export const SignDigitalStatementContext = createContext(false);
export function useSignDigitalStatement() {
    return useContext(SignDigitalStatementContext);
}
//# sourceMappingURL=signing-measurement.js.map